import CreditCardValidator from 'card-validator';
import creditCardType, {types as CardType} from "credit-card-type";
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import 'react-responsive-modal/styles.css';
import { Modal } from 'react-responsive-modal';

import Button from 'commons/components/Button';
import FormikInputField from 'commons/components/FormFields/Formik/FormikInputField';
import FormikMaskedInputField from 'commons/components/FormFields/Formik/FormikMaskedInputField';
import FormikNumberField from 'commons/components/FormFields/Formik/FormikNumberField';
import MetsIcon from 'commons/components/MetsIcon';
import ItemSelectField from 'commons/components/FormFields/ItemSelectField';
import { fetchCountryList, fetchRegion, validateZipPostalCode } from 'commons/resources/api/regions';
import securityCodeImg from 'commons/resources/images/securityCode.png';

import PaymentModeToggle from "UserRegistration/components/PaymentModeToggle"
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { isSaveButtonDisabled } from 'UserRegistration/contexts/configs/componentConfig';
import { fetchCreditCardTermsConditionsData, fetchPriceTaxesData, fetchBillingInfoDetails} from 'UserRegistration/resources/api/configurationData';
import { validateSingleField, resetErrorForField } from 'UserRegistration/resources/utils/ValidateUtils';
import { saveSection } from 'UserRegistration/resources/utils/saveUtils';
import RegistrationSection from 'UserRegistration/components/RegistrationSteps/RegistrationSection';

/**
 * Section that displays the billing information form to the user. Captures user billing information.
 * 
 * @param {object} props 
 */
function BaseBillingInformation(props) {

    const CVV_CODE_LENGTH = [3, 4];
    const EXP_DATE_PATTERN = /^((0[1-9])|(1[0-2]))\/(\d{2})$/;
    const EXP_DATE_MAX_YEAR_DIFF = 25;

    const { componentConfig } = useContext(ConfigurationContext);
    const isAddContactContext = props.isAddContactContext;
    const { registrationData, setRegistrationData } = useContext(DataContext);
    /* A ref is created pointing to registrationData because the variable is updated from within useEffect. 
     * Because it's a context state variable, without a ref, there is a risk of a stale version of the variable being accessed 
     * and subsequently used to update an already more recent version of the state. This is particularly dangerous because 
     * many different concurrently running components update the registrationData object.
     */
    const registrationDataRef = useRef(registrationData);
    const [ccType, setCCType] = useState("");
    const closeIcon = (<MetsIcon name="svg_close" />);
    const [countryList, setCountryList] = useState([]);
    const [formikInitialValues, setFormikInitialValues] = useState({
        cardHolderName: "",
        cardNumber: "",
        expiryDate: "",
        securityCode: "",
        address: "",
        countryId: "",
        stateProvinceId: "",
        city: "",
        zipCode: "",
        creditCardTermsAndConditionsAccepted: false,
        payWithNewCard: true
    });
    const [selectedCountry, setSelectedCountry] = useState({});
    const [selectedStateProvinceId, setSelectedStateProvinceId] = useState();
    const [showTermsAndConditionsLightBox, setShowTermsAndConditionsLightBox] = useState(false);
  
    const existingCCType = registrationData.registrationModel.billingInformation.existingCCType;
    const lastFourDigits = registrationData.registrationModel.billingInformation.existingCCLastFourDigits;
    const [showPaymentMethodToggle, setShowPaymentMethodToggle] = useState(false);
    const isPayingWithNewCreditCard = registrationData.registrationModel.billingInformation.selectedPayWithNewCard;
    
    const isStateProvAndZipEnabled = selectedCountry.children && selectedCountry.children.length > 0;
    const { t, i18n } = useTranslation(componentConfig.i18nNamespaces);

    useEffect(() => {
        registrationDataRef.current = registrationData;
    });

    // Fetch initial data for billing information
    useEffect(() => {
        
        fetchCreditCardTermsConditionsData().then((response) => {

            if (response) {

                const registrationDataCopy = Object.assign({}, registrationDataRef.current);

                registrationDataCopy.creditCardInfo.termsAndConditions = response;
                
                setRegistrationData(registrationDataCopy);

            }

        });

        // Fetch the initial list of countries.
        fetchCountryList().then((response) => {

            const initialValuesCopy = Object.assign({}, formikInitialValues);

            initialValuesCopy.countryId = response.data[0].id;

            // Check if we can find the default country based on the isoCode.
            response.data.forEach((c) => {
                if (c.isoCode === props.defaultBillingAddressCountryCode) {
                    initialValuesCopy.countryId = c.id;
                }
            });

            // Set the list of available countries.
            setCountryList(response.data);
            
            // Update the form's initial values.
            setFormikInitialValues(initialValuesCopy);

            if (!isAddContactContext) {
                // Set the Default selected country as the first in the list and handle change if there is no existing one.
                handleCountryChange(initialValuesCopy.countryId);
            }

        }).finally(() => {

            updateDefaultAddressInfo();

        });

    }, []);

    // If isBillingRequired flag changes, we should refetch the default/existing address info.
    useEffect(() => {
        updateDefaultAddressInfo();
    }, [registrationData.metadata.isBillingRequired]);

    const updateDefaultAddressInfo = () => {

        if (isAddContactContext) {

            fetchBillingInfoDetails().then((response) => {

                if (response) {

                    const registrationDataCopy = Object.assign({}, registrationDataRef.current);
                    
                    registrationDataCopy.registrationModel = updateRegistrationModelContext(response);
                    
                    // Copy over the fields we need - CC Info
                    if (response.creditCardId) {

                        // Need to update Formik values for validation.

                        // If we have a valid non expired credit card we want them to have the Use Existing Card selected by default instead.
                        const initialValuesCopy = Object.assign({}, formikInitialValues);
                        initialValuesCopy.payWithNewCard = false;
                        setFormikInitialValues(initialValuesCopy);

                        // Valid card means we select the use existing card option.
                        registrationDataCopy.registrationModel.billingInformation.selectedPayWithNewCard = false;
                        registrationDataCopy.registrationModel.billingInformation.existingCCLastFourDigits = response.lastFourDigits;
                        registrationDataCopy.registrationModel.billingInformation.existingCCType = response.cardType;

                        setShowPaymentMethodToggle(true);

                    }

                    const countryId = response.countryId;
                    const orgStateProvinceId = response.stateProvinceId?.toString();

                    fetchRegion(countryId).then((regionResponse) => {
                        setSelectedCountry(regionResponse.data);
                    }).finally(() => {

                        setSelectedStateProvinceId(orgStateProvinceId);

                        props.formikRef?.current?.setFieldValue('stateProvinceId', orgStateProvinceId, false);
                        props.formikRef?.current?.setFieldValue('countryId', countryId, false);
                        props.formikRef?.current?.setFieldValue('address', response.address, false);
                        props.formikRef?.current?.setFieldValue('city', response.city, false);
                        props.formikRef?.current?.setFieldValue('zipCode', response.zipCode || '', false);

                        updatePriceTaxesContextValuesOnChange('stateProvinceId', orgStateProvinceId);

                        setRegistrationData(registrationDataCopy);
                        
                    });
                }
            });
        }

    };

    const yupValidationSchema = Yup.object({
        selectedPayWithNewCard: Yup.boolean(),
        cardHolderName: Yup.string()
            .when('selectedPayWithNewCard', {
               is: () => { return isPayingWithNewCreditCard;},
               then: Yup.string()
                    .required(t('error.required'))
            }),
        cardNumber: Yup.string()
            .when('selectedPayWithNewCard', {
                is: () => { return isPayingWithNewCreditCard;},
                then: Yup.string()
                    .required(t('error.required'))
                    .test('test-number', t('error.creditCard.number.invalid'), value => CreditCardValidator.number(value).isValid)
            }),
        expiryDate: Yup.string()
            .when('selectedPayWithNewCard', {
                is: () => { return isPayingWithNewCreditCard;},
                then: Yup.string()
                    .test('required', t('error.required'), value => value === undefined || value === null ? false : (value.trim() !== '/' && value.trim() !== ''))
                    .matches(EXP_DATE_PATTERN, t('error.creditCard.expiryDate.invalid'))
                    .test('available-years', t('error.creditCard.expiryDate.invalid'), value => moment(value, 'MM/YY').endOf('year').diff(moment(), 'years') <= EXP_DATE_MAX_YEAR_DIFF)
                    .test('test-future', t('error.creditCard.expiryDate.past'), value => moment(value, 'MM/YY').endOf('month').isSameOrAfter(moment()))
            }),
        securityCode: Yup.string()
            .when('selectedPayWithNewCard', {
                is: () => { return isPayingWithNewCreditCard;},
                then: Yup.string()
                    .required(t('error.required'))
                    .test('test-number', t('error.creditCard.cvv.invalid'), value => CreditCardValidator.cvv(value, CVV_CODE_LENGTH).isValid)
        }),
        address: Yup.string()
            .required(t('error.required')),
        countryId: Yup.string()
            .required(t('error.required')),
        stateProvinceId: Yup.string()
            .when('countryId', {
                is: () => { return isStateProvAndZipEnabled; },
                then: Yup.string().required(t('error.required'))
            }),
        city: Yup.string()
            .required(t('error.required')),
        zipCode: Yup.string()
            .when('countryId', {
                is: () => { return isStateProvAndZipEnabled; },
                then: Yup.string()
                    .required(t('error.required'))
                    .test('zip-code-format', 'Invalid Zip/Postal Format', function(value) {

                        if (value) {

                            return validateZipPostalCode(value, selectedCountry.isoCode).then((data) => {

                                if (data && data.length > 0) {
                                    return this.createError({ message: data[0].defaultMessage });
                                }
    
                                return true;
    
                            });

                        }

                    })
            }),
        creditCardTermsAndConditionsAccepted: Yup.boolean()
    });

    const updateCCType = (event) => {
        const cardNumber = event.target.value;
        const ccTypeArr = creditCardType(cardNumber);
        // When there is only one item in the array returned, then we have the type
        if (ccTypeArr.length == 1) {
            setCCType(ccTypeArr[0].type);
        } else {
            setCCType("");
        }
    }

    const updateCreditCardContextValuesOnChange = (event) => {

        const registrationDataCopy = Object.assign({}, registrationData);
        
        const cardNumber = event.target.value;

        if (CreditCardValidator.number(cardNumber).isValid) {

            const ccLastFourDigits = cardNumber.substr(cardNumber.length - 4);
            const ccType = CreditCardValidator.number(cardNumber).card.niceType;

            registrationDataCopy.creditCardInfo.type = ccType;
            registrationDataCopy.creditCardInfo.lastFourDigits = ccLastFourDigits;

        } else {
            registrationDataCopy.creditCardInfo.type = null;
            registrationDataCopy.creditCardInfo.lastFourDigits = null;
        }

        setRegistrationData(registrationDataCopy);
    }

    // Called when country, state/province or zip code is changed
    const updatePriceTaxesContextValuesOnChange = (fieldName, value) => {

        // Update registration context
        const registrationDataCopy = Object.assign({}, registrationDataRef.current);

        registrationDataCopy.registrationModel.billingInformation[fieldName] = value;

        // If we are in synchronized subscription item selector mode: Only call the calculate when we have 1 or more items selected.
        // MERX sends ONLY the selected group to the server and the server will auto select the first one...
        if (componentConfig.componentDefinition.PublicPackageSelector.props.showSynchronizedSubscriptionItemSelector && 
                (registrationDataCopy.invoiceItemIDs.length === 0)) {

            // Reset all values.
            registrationDataCopy.priceTaxesInfo.subTotal = 0;
            registrationDataCopy.priceTaxesInfo.total = 0;
            registrationDataCopy.priceTaxesInfo.discountAmount = 0;
            registrationDataCopy.priceTaxesInfo.discountPercentage = 0;
            registrationDataCopy.priceTaxesInfo.totalBeforeTaxes = 0;
            registrationDataCopy.priceTaxesInfo.recurringSubTotal = 0;
            registrationDataCopy.priceTaxesInfo.taxes = [];

            setRegistrationData(registrationDataCopy);

            return;
        }

        fetchPriceTaxesData(registrationDataCopy).then((response) => {

            if (response) {
                const registrationDataCopy = Object.assign({}, registrationDataRef.current);
                
                // Set the sub-total amount
                registrationDataCopy.priceTaxesInfo.subTotal = response.subTotal;
        
                // Set the total amount 
                registrationDataCopy.priceTaxesInfo.total = response.total;

                // Set the total amount BEFORE taxes
                registrationDataCopy.priceTaxesInfo.totalBeforeTaxes = response.totalBeforeTaxes;

                // Set the next payment amount
                registrationDataCopy.priceTaxesInfo.recurringSubTotal = response.recurringSubTotal;

                // Set the volume discount amount.
                registrationDataCopy.priceTaxesInfo.discountAmount = response.userRegistrationDiscountInformationDto.volumeDiscountAmount;
            
                // Set the volume discount percentage.
                registrationDataCopy.priceTaxesInfo.discountPercentage = response.userRegistrationDiscountInformationDto.volumeDiscountPercentage;
                
                // Amount discounted by the provided promo code.
                registrationDataCopy.priceTaxesInfo.promoDiscountAmount = response.userRegistrationDiscountInformationDto.promoCodeDiscountAmount;
        
                // Empty the taxes list before adding in
                registrationDataCopy.priceTaxesInfo.taxes = [];
        
                response.macTaxInfoDetails.forEach((taxInfo) => {
                    registrationDataCopy.priceTaxesInfo.taxes.push({
                        name: taxInfo.taxName,
                        rate: taxInfo.taxRate,
                        amount: taxInfo.taxAmount
                    });
                });

                setRegistrationData(registrationDataCopy);
            }

        });
        
    }

    const formikOnSubmit = (values) => {

        updateContextWithValues(values);

        // Save the section.
        saveSection(componentConfig, registrationData, setRegistrationData);

    };

    const updateContextWithValues = (values) => {


        const registrationDataCopy = Object.assign({}, registrationData);
        // Clear errors in case registrationData was not updated yet (state setting is asynchronous)
        registrationDataCopy.validationErrors = [];
        const registrationModel = updateRegistrationModelContext(values);
        const creditCardInfo = updateCreditCardInfoContext(values);

        setRegistrationData({
            ...registrationDataCopy, 
            registrationModel: registrationModel, 
            creditCardInfo: creditCardInfo
        });
    }

    const updateRegistrationModelContext = (values) => {

        const registrationModel = Object.assign({}, registrationData.registrationModel);

        registrationModel.billingInformation.address = values.address;
        registrationModel.billingInformation.countryId = values.countryId;
        registrationModel.billingInformation.stateProvinceId = values.stateProvinceId;
        registrationModel.billingInformation.city = values.city;
        registrationModel.billingInformation.zipCode = values.zipCode;

        return registrationModel;

    }

    const updateCreditCardInfoContext = (values) => {

        const creditCardInfo = registrationData.creditCardInfo;

        creditCardInfo.mpgCardtype = convertCardTypeToMpgCardType(ccType);
        creditCardInfo.cardHolderName = values.cardHolderName;
        creditCardInfo.cardNumber = values.cardNumber;
        creditCardInfo.expirationMonth = values.expiryDate.split('/')[0];
        creditCardInfo.expirationYear = '20' + values.expiryDate.split('/')[1];
        creditCardInfo.securityCode = values.securityCode;
        
        return creditCardInfo;

    }

    const convertCardTypeToMpgCardType = (cardType) => {
        
        switch(cardType) {
            case CardType.VISA:
                return "VISA";
            case CardType.MASTERCARD:
                return "MC";
            case CardType.AMERICAN_EXPRESS:
                return "AMEX";
            default:
                return null;
        }
        
    }

    function toggleCreditCardPaymentMode(payingWithNewCreditCard, setFieldValue) {
        const registrationDataCopy = Object.assign({}, registrationData);
        registrationDataCopy.registrationModel.billingInformation.selectedPayWithNewCard = payingWithNewCreditCard;
        setRegistrationData(registrationDataCopy);
        setFieldValue('payWithNewCard', payingWithNewCreditCard, false);
    }

    function getCurrentCreditCardDisplay() {
         return   (
            <>
                <MetsIcon name="svg_VISA" className={ccType == CardType.VISA ? 'colorVISA' : ''} key="VISA" />
                {ccType == CardType.MASTERCARD ? <MetsIcon name="svg_color_MC" className="colorMC" key="color_MC" /> : <MetsIcon name="svg_MC" key="MC" />}
                <MetsIcon name="svg_AE" className={ccType == CardType.AMERICAN_EXPRESS ? 'colorAE': ''} key="AE" />
            </>
        );
    }

    /**
     * Handles the change of the selected country.
     * 
     * @param {string} countryId The server ID of the country to set.
     * @param {function} setFieldValue Optional function that should be Formik's setFieldValue. Supply when appropriate.
     */
    async function handleCountryChange(countryId, setFieldValue, setFieldTouched) {

        return fetchRegion(countryId).then((response) => {

            setSelectedCountry(response.data);
            updatePriceTaxesContextValuesOnChange('countryId', countryId);

        }).finally(() => {

            if (setFieldValue) {
                // Clear the stateProvince and zipCode when the country changes.
                setSelectedStateProvinceId('');
                setFieldValue('countryId', countryId, false);
                setFieldValue('stateProvinceId', '', false);
                setFieldValue('zipCode', '', false);
                setFieldTouched('zipCode', false, true);
            }
        });

    }

    return (

        registrationData.metadata.isBillingRequired && 
        
        <Formik
            enableReinitialize={true}
            initialValues={formikInitialValues}
            validationSchema={yupValidationSchema}
            innerRef={props.formikRef}
            onSubmit={formikOnSubmit}
            validateOnChange={false}
            validateOnBlur={false}>

            {formikProps => (

                <Form>

                    <RegistrationSection
                        className="billing-info-section"
                        id="billingInfoSection"
                        saveButtonDisabled={isSaveButtonDisabled(registrationData, componentConfig)}
                        saveButtonOnClick={formikProps.submitForm}
                        sectionNumber={props.sectionNumber}
                        title={t('billingInformation')}
                        titleExtraContent={(() => {
                            return !showPaymentMethodToggle && getCurrentCreditCardDisplay();
                        })()}
                    >

                        {showPaymentMethodToggle &&
                            <div className="section-fields payment-toggle-fields">
                                <PaymentModeToggle 
                                    existingCardLabel={t('payWithExistingCard', {existingCCType: t(`mpg_${existingCCType}`), lastFourDigits: lastFourDigits})} 
                                    existingCCType={existingCCType}    
                                    isPayingWithNewCreditCard={registrationData.registrationModel.billingInformation.selectedPayWithNewCard}
                                    ccType={ccType} 
                                    newCardLabel={t('saveAndPayWithNewCard')}
                                    creditCardToggleAction={(newCCFlag) => {toggleCreditCardPaymentMode(newCCFlag, formikProps.setFieldValue)}}
                                    setFieldValue={formikProps.setFieldValue}
                                />
                            </div>
                        }

                        {registrationData.registrationModel.billingInformation.selectedPayWithNewCard && <>
                            <div className="section-fields">
                                <Field
                                    component={FormikInputField}
                                    id="inputBillingInfoCardHolderName"
                                    label={t('cardHolderName')}
                                    maxLength={30}
                                    name="cardHolderName"
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                    rowClassName="large-field" />

                                <Field
                                    component={FormikInputField}
                                    id="inputBillingInfoCardNumber"
                                    label={t('cardNumber')}
                                    maxLength="19"
                                    name="cardNumber"
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                    onChange={(event) => {
                                        updateCreditCardContextValuesOnChange(event);
                                        updateCCType(event);
                                    }} 
                                    placeholder="0000 0000 0000 0000"
                                />

                                <Field
                                    component={FormikMaskedInputField}
                                    id="inputBillingInfoExpiryDate"
                                    label={t('expiryDate')}
                                    mask={[/\d/,/\d/,'/',/\d/,/\d/]}
                                    name="expiryDate"
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                    rowClassName="small-field"
                                    placeholder={t('expiryDate.placeholder')} />

                                <Field
                                    autoComplete="off"
                                    component={FormikNumberField}
                                    fieldIcon={<MetsIcon name="svg_help" data-tip data-for="securityCodeTooltip" />}
                                    id="inputBillingInfoSecurityCode"
                                    label={t('securityCode')}
                                    maxLength={4}
                                    name="securityCode"
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                    rowClassName="small-field"
                                    placeholder="CVV" />

                            </div>

                            <ReactTooltip id='securityCodeTooltip' type="light" place="bottom" border={true} backgroundColor="#fff" borderColor="#DEE3ED">
                                <img src={securityCodeImg} alt="Security Code" />
                            </ReactTooltip>

                            <h3 id="billingAddressHeading" className="sub-section-title">
                                {t('billingAddress.title')}
                            </h3>

                            <div className="section-fields">

                                <Field id="inputBillingInfoAddress" component={FormikInputField} name="address" label={t('address')} rowClassName="large-field" maxLength={300} 
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} disabled={isAddContactContext}/>
                                
                                <Field id="inputBillingInfoCity" component={FormikInputField} name="city" label={t('city')} maxLength={50}
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} disabled={isAddContactContext}/>

                                <Field id="inputBillingInfoZipCode" component={FormikInputField} name="zipCode" label={t('zipCode')} disabled={!isStateProvAndZipEnabled || isAddContactContext} maxLength={12} onChange={(e) => { updatePriceTaxesContextValuesOnChange(e.target.name, e.target.value); }}
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />

                                <ItemSelectField 
                                    idPrefix="itemSelectBillingInfoCountry"
                                    items={countryList.map((country) => {
                                        return { label: country.name, value: country.id.toString() };
                                    })}
                                    disabled={isAddContactContext}
                                    error={formikProps.errors['countryId']}
                                    label={t('country')}
                                    name="countryId"
                                    onBlur={() => validateSingleField("countryId", props.formikRef.current) }
                                    onChange={(selectedItem) => {
                                        resetErrorForField('countryId', props.formikRef.current);
                                        handleCountryChange(selectedItem.value, formikProps.setFieldValue, formikProps.setFieldTouched);
                                    }}
                                    placeholder={ t("chooseCountry") }
                                    selectedItem={selectedCountry.id ? selectedCountry.id.toString() : null}
                                    touched={formikProps.touched['countryId']}
                                />

                                <ItemSelectField 
                                    disabled={!isStateProvAndZipEnabled || isAddContactContext}
                                    error={formikProps.errors['stateProvinceId']}
                                    items={selectedCountry.children ? selectedCountry.children.map((region) => {
                                            return { label: region.name, value: region.id.toString() };
                                        }) : []
                                    }
                                    label={t('stateProvince')}
                                    name="stateProvinceId"
                                    onBlur={() => validateSingleField("stateProvinceId", props.formikRef.current) }
                                    onChange={(selectedItem) => {
                                        formikProps.setFieldValue('stateProvinceId', selectedItem.value, false);
                                        resetErrorForField('stateProvinceId', props.formikRef.current);
                                        setSelectedStateProvinceId(selectedItem.value);
                                        updatePriceTaxesContextValuesOnChange('stateProvinceId', selectedItem.value);
                                    }}
                                    placeholder={ t("chooseStateProv") }
                                    selectedItem={selectedStateProvinceId}
                                    touched={formikProps.touched['stateProvinceId']}
                                />

                            </div>
                        </>
                        }

                        {props.showCreditCardAgreementCheckbox &&
                            <>
                                <div className="section-fields">
                                    <Field component={FormikInputField} name="creditCardTermsAndConditionsAccepted" type="checkbox" className="input-checkbox"
                                        onChange={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                        label={[
                                            t('cc.terms.conditions.1'),
                                            <a key="ccTermsConditionsLink" href="#" onClick={(e) => { setShowTermsAndConditionsLightBox(true); e.preventDefault(); }}>{t('cc.terms.conditions.2')}</a>,
                                            t('cc.terms.conditions.3')
                                    ]} />

                                </div>

                                <Modal open={showTermsAndConditionsLightBox} onClose={() => { setShowTermsAndConditionsLightBox(false); }} center closeIcon={closeIcon}>
                                    <h1>{t('creditCard.termsAndConditions.title')}</h1>
                                    {registrationData.creditCardInfo.termsAndConditions && <p>{registrationData.creditCardInfo.termsAndConditions[i18n.language.toUpperCase()]} </p>}
                                    <div className="react-responsive-modal-button-bar">
                                        <Button label={t('close')} onClick={() => { setShowTermsAndConditionsLightBox(false); }} />
                                    </div>
                                </Modal>
                            </>
                        }

                    </RegistrationSection>

                </Form>

            )}

        </Formik>

    );

}

BaseBillingInformation.propTypes = {
    /** The default selected country. The application expects a country ISO 2 code (ex: CA, US). */
    defaultBillingAddressCountryCode: PropTypes.string,
    /** A reference to the Formik component. */
    formikRef: PropTypes.any,
    /** Used to determine if this is the AddContact Context */
    isAddContactContext: PropTypes.bool,
    /** The total number of configurable sections currently loaded in the instance of UserRegistration. */
    sectionCount: PropTypes.number,
    /** The position of this section in relation to other sections in the entire instance of UserRegistration. */
    sectionNumber: PropTypes.number,
    /** Whether or not to display the credit card agreement checkbox. Some portals may not have agreements to display (US portals). */
    showCreditCardAgreementCheckbox: PropTypes.bool
    
};

export default BaseBillingInformation;
